export interface SceneTreeNodeLike {
  id: string;
  nodeType?: string;
  children: readonly SceneTreeNodeLike[];
}

export function sceneTreeNodeContains(node: SceneTreeNodeLike, targetId: string, visited: Set<string> = new Set()): boolean {
  if (visited.has(node.id)) return false;
  visited.add(node.id);
  if (node.id === targetId) return true;
  return node.children.some(child => sceneTreeNodeContains(child, targetId, visited));
}

export function canReparentSceneTreeNode(node: SceneTreeNodeLike, parent: SceneTreeNodeLike | null | undefined): boolean {
  if (!parent) return true;
  if (parent.id === node.id) return false;
  return !sceneTreeNodeContains(node, parent.id);
}

/**
 * Validate node-type parenting rules.
 * - folders can only live under scene/folder
 * - actors can live under scene/folder/actor
 * - scene cannot be parented and can only exist at the top level
 */
export function canParentSceneTreeNode(node: SceneTreeNodeLike, parent: SceneTreeNodeLike | null | undefined): boolean {
  const nodeType = node.nodeType ?? 'actor';
  const parentType = parent?.nodeType;

  if (!parent) {
    return nodeType === 'scene';
  }

  if (nodeType === 'scene') {
    return false;
  }

  if (nodeType === 'folder') {
    return parentType === 'scene' || parentType === 'folder';
  }

  return parentType === 'scene' || parentType === 'folder' || parentType === 'actor';
}

/**
 * Keep only top-level selected nodes so parent+child multi-select moves do not
 * execute duplicate reparent/reorder operations.
 */
export function filterTopLevelSceneTreeNodes<T extends { id: string }>(
  nodes: readonly T[],
  isDescendantOf: (nodeId: string, possibleAncestorId: string) => boolean,
): T[] {
  return nodes.filter(node =>
    !nodes.some(other => other.id !== node.id && isDescendantOf(node.id, other.id))
  );
}

/**
 * Adjusts a desired child insert index when moving within the same parent array.
 * Required because removing an item before the insert slot shifts later indices down by one.
 */
export function adjustMoveInsertIndex(currentIndex: number, insertPosition: number): number {
  return currentIndex < insertPosition ? insertPosition - 1 : insertPosition;
}
